<template>
  <ListItem :has-error="hasError" @click="showModal = true">
    <ListItemColumn :label="t('option')">
      {{ printValue() }}
    </ListItemColumn>
    <ListItemColumn
      :label="
        t(value.pricingType === VehicleExtraOptionPricingType.FIXED ? 'fixPrice' : 'pricePerDay')
      "
      class="whitespace-break-spaces"
    >
      {{ printPrice() }}
    </ListItemColumn>
    <DeleteButton @click.stop="$emit('delete')" />
  </ListItem>

  <Modal v-model="showModal" content-class="!overflow-y-visible">
    <OptionForm
      :price-range-index="priceRangeIndex"
      :option-index="optionIndex"
      :extra-type="extraType"
      @close="showModal = false"
    />
  </Modal>
</template>

<script setup lang="ts">
import {
  type EditAutoAssignExtraOptionDto,
  type EditAutoAssignVehicleExtraDto,
  VehicleExtraOptionPricingType,
  VehicleExtraType,
} from '@carvia/ros-client-types';
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';
import { formatCurrency } from '@/utils/format-numbers.ts';
import ListItem from '../ui/ListItem.vue';
import ListItemColumn from '@/views/yield-management/ui/ListItemColumn.vue';
import DeleteButton from '@/views/yield-management/ui/DeleteButton.vue';
import { ref } from 'vue';
import OptionForm from '@/views/yield-management/edit-extras/OptionForm.vue';
import Modal from '@/components/Modal.vue';

const props = defineProps<{
  priceRangeIndex: number;
  optionIndex: number;
  extraType: EditAutoAssignVehicleExtraDto['type'];
  hasError?: boolean;
}>();

defineEmits<{
  (e: 'delete'): void;
}>();

const showModal = ref(false);

const { value } = useField<EditAutoAssignExtraOptionDto>(
  `priceRanges.${props.priceRangeIndex}.options.${props.optionIndex}`,
);

const printPrice = () => {
  if (value.value.pricingType === VehicleExtraOptionPricingType.FIXED) {
    return formatCurrency(value.value.price ?? 0);
  }
  const priceLines =
    value.value.pricePerDay?.map((price) =>
      t('pricePerDayValues', { count: price.fromDay, price: formatCurrency(price.pricePerDay) }),
    ) ?? [];
  return priceLines?.length ? priceLines.join('\n') : t('noPrices');
};

const printValue = () => {
  const option = value.value;
  switch (props.extraType) {
    case VehicleExtraType.ADDITIONAL_DRIVER:
    case VehicleExtraType.YOUNG_DRIVER:
    case VehicleExtraType.EXTRA_KM:
      return t(`values.${props.extraType}`, { count: parseInt(option.value) });
    case VehicleExtraType.DEDUCTIBLE:
      return t(`values.${props.extraType}`, { value: formatCurrency(parseFloat(option.value)) });
    default:
      return t(option.value === 'true' ? 'yes' : 'no');
  }
};

const { t } = useI18n();
</script>

<i18n lang="json">
{
  "en": {
    "fixPrice": "Fix price",
    "pricePerDay": "Price per day",
    "pricePerDayValues": "From {count} days: {price}/day | From {count} day: {price}/day",
    "option": "Option",
    "values": {
      "ADDITIONAL_DRIVER": "{count} additional driver | {count} additional drivers",
      "YOUNG_DRIVER": "{count} young driver | {count} young drivers",
      "DEDUCTIBLE": "{value} deductible",
      "EXTRA_KM": "{count} km"
    },
    "yes": "Yes",
    "no": "No",
    "noPrices": "No prices"
  },
  "de": {
    "fixPrice": "Fixpreis",
    "pricePerDay": "Preis pro Tag",
    "pricePerDayValues": "Ab {count} Tag: {price}/Tag | Ab {count} Tagen: {price}/Tag",
    "option": "Option",
    "values": {
      "ADDITIONAL_DRIVER": "{count} Zusatzfahrer",
      "YOUNG_DRIVER": "{count} Jungfahrer",
      "DEDUCTIBLE": "{value} SB",
      "EXTRA_KM": "{count} km"
    },
    "yes": "Ja",
    "no": "Nein",
    "noPrices": "Keine Preise"
  },
  "es": {
    "fixPrice": "Precio fijo",
    "pricePerDay": "Precio por día",
    "pricePerDayValues": "Desde {count} día: {price}/día | Desde {count} días: {price}/día",
    "option": "Opción",
    "values": {
      "ADDITIONAL_DRIVER": "{count} conductor adicional | {count} conductores adicionales",
      "YOUNG_DRIVER": "{count} conductor joven | {count} conductores jóvenes",
      "DEDUCTIBLE": "Franquicia {value}",
      "EXTRA_KM": "{count} km"
    },
    "yes": "Sí",
    "no": "No",
    "noPrices": "Sin precios"
  }
}
</i18n>
